// Typed API-client voor de Leiderschap-module. Hergebruikt de globale axios-
// instance uit AuthContext (Authorization-header wordt daar via een
// interceptor gezet) -- geen eigen axios-instantie nodig.
import axios from 'axios';
import type {
  ActionItem,
  ActionItemInput,
  AgendaItem,
  AgendaItemInput,
  Decision,
  DecisionInput,
  LeadershipDocument,
  LeadershipMember,
  LeadershipMeeting,
  LeadershipMeetingInput,
  LeadershipMinutes,
  LeiderschapNotificationSettings,
  LeiderschapNotificationSettingsInput,
} from './types';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/leiderschap`;

export const leiderschapApi = {
  listMeetings: () => axios.get<LeadershipMeeting[]>(`${BASE}/meetings`).then((r) => r.data),
  getMeeting: (id: string) => axios.get<LeadershipMeeting>(`${BASE}/meetings/${id}`).then((r) => r.data),
  createMeeting: (data: LeadershipMeetingInput) => axios.post<LeadershipMeeting>(`${BASE}/meetings`, data).then((r) => r.data),
  updateMeeting: (id: string, data: Partial<LeadershipMeetingInput>) =>
    axios.put<LeadershipMeeting>(`${BASE}/meetings/${id}`, data).then((r) => r.data),
  deleteMeeting: (id: string) => axios.delete(`${BASE}/meetings/${id}`),

  listAgendaItems: (meetingId: string) => axios.get<AgendaItem[]>(`${BASE}/meetings/${meetingId}/agenda`).then((r) => r.data),
  createAgendaItem: (meetingId: string, data: AgendaItemInput) =>
    axios.post<AgendaItem>(`${BASE}/meetings/${meetingId}/agenda`, data).then((r) => r.data),
  updateAgendaItem: (meetingId: string, itemId: string, data: Partial<AgendaItemInput>) =>
    axios.put<AgendaItem>(`${BASE}/meetings/${meetingId}/agenda/${itemId}`, data).then((r) => r.data),
  deleteAgendaItem: (meetingId: string, itemId: string) => axios.delete(`${BASE}/meetings/${meetingId}/agenda/${itemId}`),

  getMinutes: (meetingId: string) => axios.get<LeadershipMinutes>(`${BASE}/meetings/${meetingId}/minutes`).then((r) => r.data),
  saveMinutes: (meetingId: string, content: string) =>
    axios.put<LeadershipMinutes>(`${BASE}/meetings/${meetingId}/minutes`, { content }).then((r) => r.data),
  finalizeMinutes: (meetingId: string) =>
    axios.post<LeadershipMinutes>(`${BASE}/meetings/${meetingId}/minutes/finalize`).then((r) => r.data),

  listDocuments: (meetingId: string) => axios.get<LeadershipDocument[]>(`${BASE}/meetings/${meetingId}/documents`).then((r) => r.data),
  uploadDocument: (meetingId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios
      .post<LeadershipDocument>(`${BASE}/meetings/${meetingId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  downloadDocument: async (meetingId: string, documentId: string, filename: string) => {
    const response = await axios.get(`${BASE}/meetings/${meetingId}/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  deleteDocument: (meetingId: string, documentId: string) => axios.delete(`${BASE}/meetings/${meetingId}/documents/${documentId}`),

  listDecisions: () => axios.get<Decision[]>(`${BASE}/decisions`).then((r) => r.data),
  createDecision: (data: DecisionInput) => axios.post<Decision>(`${BASE}/decisions`, data).then((r) => r.data),
  updateDecision: (id: string, data: Partial<DecisionInput>) =>
    axios.put<Decision>(`${BASE}/decisions/${id}`, data).then((r) => r.data),

  listActionItems: (agendaItemId?: string) =>
    axios
      .get<ActionItem[]>(`${BASE}/action-items`, agendaItemId ? { params: { agenda_item_id: agendaItemId } } : undefined)
      .then((r) => r.data),
  listLeadershipMembers: (meetingId?: string) =>
    axios
      .get<LeadershipMember[]>(`${BASE}/members`, meetingId ? { params: { meeting_id: meetingId } } : undefined)
      .then((r) => r.data),
  createActionItem: (data: ActionItemInput) => axios.post<ActionItem>(`${BASE}/action-items`, data).then((r) => r.data),
  updateActionItem: (id: string, data: Partial<ActionItemInput>) =>
    axios.put<ActionItem>(`${BASE}/action-items/${id}`, data).then((r) => r.data),

  getNotificationSettings: () => axios.get<LeiderschapNotificationSettings>(`${BASE}/notification-settings`).then((r) => r.data),
  updateNotificationSettings: (data: LeiderschapNotificationSettingsInput) =>
    axios.put<LeiderschapNotificationSettings>(`${BASE}/notification-settings`, data).then((r) => r.data),
};
